import { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams, useNavigate } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import BackToTop from './components/ui/BackToTop';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import Stories from './pages/Stories';
import StoryDetail from './pages/StoryDetail';
import Science from './pages/Science';
import Carnivore from './pages/Carnivore';
import Fasting from './pages/Fasting';
import About from './pages/About';
import Programs from './pages/Programs';
import Performance from './pages/Performance';
import Kitchen from './pages/Kitchen';
import Join from './pages/Join';
import Book from './pages/Book';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Disclaimer from './pages/Disclaimer';
import QuickStart from './pages/QuickStart';
import FAQ from './pages/FAQ';
// Read page removed — replaced by /reader/:bookId
import Success from './pages/Success';
import Dashboard from './pages/Dashboard';
import Login, { AuthVerify, DiscordCallback } from './pages/Login';
import Account from './pages/Account';
import NotFound from './pages/NotFound';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import FreeGuide from './pages/FreeGuide';
import Challenge from './pages/Challenge';
import Referral from './pages/Referral';
import Pricing from './pages/Pricing';
import Coaching from './pages/Coaching';
import Webinar from './pages/Webinar';
import Upsell from './pages/Upsell';
import AdminRevenue from './pages/AdminRevenue';
import { ReaderLayout } from '@fc/reader';
import { AuthProvider, ProtectedRoute, useAuth } from './components/auth';
import { createReaderAdapter } from './lib/reader-adapter';
import './styles/index.css';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Routes that should not show navbar/footer (only the reader for distraction-free reading)
const FULLSCREEN_ROUTES = ['/reader', '/login', '/auth/verify', '/auth/discord', '/free'];

function useIsFullscreenRoute() {
  const { pathname } = useLocation();
  return FULLSCREEN_ROUTES.some(route => pathname.startsWith(route));
}

// Reader wrapper that bridges react-router params to the library component
function ReaderPage() {
  const { bookId } = useParams<{ bookId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dataProvider = useMemo(() => createReaderAdapter(), []);

  if (!bookId) return null;

  return (
    <ReaderLayout
      bookId={bookId}
      dataProvider={dataProvider}
      user={user ? { id: user.id, name: user.name, email: user.email || '' } : null}
      onNavigateHome={() => navigate('/dashboard')}
    />
  );
}

function AppContent() {
  const isFullscreen = useIsFullscreenRoute();

  return (
    <div className="app">
      {!isFullscreen && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/carnivore" element={<Carnivore />} />
          <Route path="/fasting" element={<Fasting />} />
          <Route path="/stories" element={<Stories />} />
          <Route path="/stories/:id" element={<StoryDetail />} />
          <Route path="/science" element={<Science />} />
          <Route path="/about" element={<About />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/kitchen" element={<Kitchen />} />
          <Route path="/join" element={<Join />} />
          <Route path="/book" element={<Book />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/quick-start" element={<QuickStart />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/free" element={<FreeGuide />} />
          <Route path="/challenge" element={<Challenge />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/coaching" element={<Coaching />} />
          <Route path="/webinar" element={<Webinar />} />
          <Route path="/upsell/:offerId" element={<Upsell />} />
          <Route path="/ref/:code" element={<Referral />} />
          <Route path="/success" element={<Success />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/verify" element={<AuthVerify />} />
          <Route path="/auth/discord" element={<DiscordCallback />} />
          {/* /read/:bookId removed — use /reader/:bookId */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {/* Redirect /library to /dashboard - unified page */}
          <Route path="/library" element={<Navigate to="/dashboard" replace />} />
          <Route path="/library/:slug" element={<Navigate to="/dashboard" replace />} />
          {/* Redirect book slug URLs to short URLs */}
          <Route path="/fasting-carnivore" element={<Navigate to="/carnivore" replace />} />
          <Route path="/nano-fasting" element={<Navigate to="/fasting" replace />} />
          <Route path="/fasting-carnivore-kitchen" element={<Navigate to="/kitchen" replace />} />
          <Route path="/fasting-carnivore-performance" element={<Navigate to="/performance" replace />} />
          <Route
            path="/dashboard/revenue"
            element={
              <ProtectedRoute>
                <AdminRevenue />
              </ProtectedRoute>
            }
          />
          {/* The Whop plan-sync page is archived (archive/whop-2026-10-15); admins use /dashboard. */}
          <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reader/:bookId"
            element={
              <ProtectedRoute requirePurchase>
                <ReaderPage />
              </ProtectedRoute>
            }
          />
          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isFullscreen && <Footer />}
      {!isFullscreen && <BackToTop />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <AppContent />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
